// mac dev environment verified 2026-06-09
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { usePermissions } from './hooks/usePermissions';
import { useActivityLog } from './hooks/useActivityLog';
import { isDevHost } from './env';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Hub from './pages/Hub';
import Admin from './pages/Admin';
import AdminDashboards from './pages/AdminDashboards';
import PatientStatusDrill from './pages/PatientStatusDrill';
import ExecPage from './pages/Exec';
import ExecAskPage from './pages/ExecAsk';
import ExecPatientsPage from './pages/ExecPatients';
import ExecStaffPage from './pages/ExecStaff';
import ExecForecastPage from './pages/ExecForecast';
import ExecMeetingsPage from './pages/ExecMeetings';
import ExecMeetingsListPage from './pages/ExecMeetingsList';
import ExecSatisfactionPage from './pages/ExecSatisfaction';
import ExecWellnessPage from './pages/ExecWellness';
import ExecFinancialPage from './pages/ExecFinancial';
import ExecSalaryPage from './pages/ExecSalary';
import ExecBillingPage from './pages/ExecBilling';
import ExecOnCallPage from './pages/ExecOnCall';
import ExecCmBonusPage from './pages/ExecCmBonus';
import OpenMeetingsReport from './pages/OpenMeetingsReport';
import ExecReportsPage from './pages/ExecReports';
import TrainingPage from './pages/Training';
import SalaryReport from './pages/SalaryReport';
import AccessDenied from './pages/AccessDenied';

// A wrapper for protected routes that checks for authentication and authorization
function PrivateRoute({ children, requiredRole }: { children: React.ReactNode; requiredRole?: string }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Dev is not for prod-only users — same Cognito pool serves both (verified 2026-05-26).
  // The backend enforces the same rule from settings.env, which cannot be spoofed;
  // this only saves them from loading a page that would answer 403 to everything.
  if (isDevHost() && !DEV_ROLES.includes(user.role)) return <AccessDenied />;
  if (requiredRole && user.role !== requiredRole) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const DEV_ROLES = ['admin', 'dev-view', 'dev-viewer'];

// A route the user reaches only if they were granted that dashboard. `id` matches the
// dashboard map, and the answer comes from the backend — the same grants every
// endpoint checks, so the page and its data can never disagree about who may look.
//
// This is navigation, not security: someone who edits their way past it still gets a
// 403 from every call the page makes.
function DashboardRoute({ id, children }: { id: string; children: React.ReactNode }) {
  const { loading, can } = usePermissions();
  return (
    <PrivateRoute>
      {loading
        ? <div className="flex items-center justify-center h-screen">Loading...</div>
        // reason="grant": nothing to do with the environment. Telling this person to
        // go to prod would send them to the same wall.
        : can(id) ? <>{children}</> : <AccessDenied reason="grant" />}
    </PrivateRoute>
  );
}

// "/" — the front door for everyone, and the target of every unknown route
// (Ohad 2026-08-12). It shows one card per dashboard this person actually holds.
//
// It used to BE the מטופלים המלא dashboard, which meant arriving without that one
// grant was indistinguishable from having no access at all. That board now lives at
// its own address and is reached from here like every other.
function HomeRoute() {
  return <PrivateRoute><Hub /></PrivateRoute>;
}

// Reports dashboard views + dwell for whoever is signed in. Inside the router (it
// needs the location) and inside the auth provider (it needs a token).
function ActivityTracker() {
  const { user } = useAuth();
  useActivityLog(!!user);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <ActivityTracker />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Therapist's own report. Public by design: the signed token in the URL
            is the authorisation, and it names one therapist and one month. */}
        <Route path="/salary/report" element={<SalaryReport />} />
        {/* Same bargain for the open-meetings backlog: one therapist, no login. */}
        <Route path="/open-meetings" element={<OpenMeetingsReport />} />
        {/* The old meetings stack (/meetings, /meetings/drill) was deleted 2026-07-26 —
            unreachable since 2026-07-07, replaced by the exec meetings drill + explorer. */}
        <Route path="/" element={<HomeRoute />} />
        {/* The patients board moved off "/" when the hub took the front door. */}
        <Route path="/patients-full" element={<DashboardRoute id="patient_base"><Dashboard /></DashboardRoute>} />
        <Route path="/dashboard/drill/:stage/:side" element={<DashboardRoute id="patient_base"><PatientStatusDrill /></DashboardRoute>} />
        <Route path="/exec" element={<DashboardRoute id="exec"><ExecPage /></DashboardRoute>} />
        {/* The assistant is a grantable dashboard of its own; on top of that it only
            ever answers about panels the person already holds. */}
        <Route path="/exec/ask" element={<DashboardRoute id="ask"><ExecAskPage /></DashboardRoute>} />
        {/* The financial drill returned 2026-08-02, rebuilt on the approved-plan table
            (business_plan) + estimated actuals — the 13.7 purge removed the MOCK one. */}
        <Route path="/exec/financial" element={<DashboardRoute id="financial"><ExecFinancialPage /></DashboardRoute>} />
        <Route path="/exec/patients" element={<DashboardRoute id="patients"><ExecPatientsPage /></DashboardRoute>} />
        <Route path="/exec/staff" element={<DashboardRoute id="staff"><ExecStaffPage /></DashboardRoute>} />
        <Route path="/exec/meetings" element={<DashboardRoute id="meetings"><ExecMeetingsPage /></DashboardRoute>} />
        <Route path="/exec/meetings/list" element={<DashboardRoute id="meetings_list"><ExecMeetingsListPage /></DashboardRoute>} />
        <Route path="/exec/satisfaction" element={<DashboardRoute id="satisfaction"><ExecSatisfactionPage /></DashboardRoute>} />
        {/* Forecast, salary and billing used to carry a prod lock (`isDevHost()`) on top
            of the grant check. Removed 2026-07-26, the last step of the permissions
            rollout: they are ordinary granted dashboards now, so who sees them is one
            decision taken on the admin screen instead of two in two different places.
            Every endpoint behind them is guarded by the same grant. */}
        <Route path="/exec/wellness" element={<DashboardRoute id="wellness"><ExecWellnessPage /></DashboardRoute>} />
        <Route path="/exec/forecast" element={<DashboardRoute id="forecast"><ExecForecastPage /></DashboardRoute>} />
        <Route path="/exec/salary" element={<DashboardRoute id="salary"><ExecSalaryPage /></DashboardRoute>} />
        <Route path="/exec/billing" element={<DashboardRoute id="billing"><ExecBillingPage /></DashboardRoute>} />
        {/* On-call schedule (Basel) — who the on-call psychiatrist is each day. */}
        <Route path="/exec/oncall" element={<DashboardRoute id="oncall"><ExecOnCallPage /></DashboardRoute>} />
        {/* Report generator — automatic mailed reports (subscriptions + send-now). */}
        <Route path="/exec/cm-bonus" element={<DashboardRoute id="cm_bonus"><ExecCmBonusPage /></DashboardRoute>} />
        <Route path="/exec/reports" element={<DashboardRoute id="reports"><ExecReportsPage /></DashboardRoute>} />
        <Route path="/training" element={<DashboardRoute id="training"><TrainingPage /></DashboardRoute>} />
        <Route path="/admin" element={<PrivateRoute requiredRole="admin"><Admin /></PrivateRoute>} />
        {/* System overview: the dashboards, what we write, what we read. Admin-only,
            and the intended home of the activity log's UI. */}
        <Route path="/admin/data" element={<PrivateRoute requiredRole="admin"><AdminDashboards /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
